import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Search as SearchIcon, MapPin, Clock, Tag, UserPlus, UserCheck, MessageSquare, SlidersHorizontal, X } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';
import Layout from '@/components/Layout';
import { useAuth } from '@/hooks/useAuth';
import { supabase } from '@/integrations/supabase/client';
import { Profile, AREAS_OF_INTEREST, FREQUENCIES } from '@/lib/types';

const staggerContainer = { hidden: {}, visible: { transition: { staggerChildren: 0.06 } } };
const staggerItem = { hidden: { opacity: 0, y: 16 }, visible: { opacity: 1, y: 0, transition: { type: 'spring', stiffness: 300, damping: 35 } } };

export default function Search() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [profiles, setProfiles] = useState<Profile[]>([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const [filterArea, setFilterArea] = useState('');
  const [filterFrequency, setFilterFrequency] = useState('');
  const [filterLocation, setFilterLocation] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [friendships, setFriendships] = useState<Record<string, { status: string; id: string; requester: string }>>({});

  const fetchProfiles = useCallback(async () => {
    setLoading(true);
    let q = supabase.from('profiles').select('*').eq('is_public', true).neq('id', user?.id ?? '');
    if (query) q = q.ilike('full_name', `%${query}%`);
    if (filterLocation) q = q.ilike('location', `%${filterLocation}%`);
    if (filterFrequency) q = q.eq('preferred_frequency', filterFrequency);
    if (filterArea) q = q.contains('areas_of_interest', [filterArea]);
    const { data } = await q.order('created_at', { ascending: false }).limit(48);
    setProfiles((data as Profile[]) || []);
    setLoading(false);
  }, [query, filterArea, filterFrequency, filterLocation, user?.id]);

  const fetchFriendships = useCallback(async () => {
    if (!user) return;
    const { data } = await supabase
      .from('friendships')
      .select('*')
      .or(`requester_id.eq.${user.id},addressee_id.eq.${user.id}`);
    const map: Record<string, { status: string; id: string; requester: string }> = {};
    (data || []).forEach((f: { requester_id: string; addressee_id: string; status: string; id: string }) => {
      const other = f.requester_id === user.id ? f.addressee_id : f.requester_id;
      map[other] = { status: f.status, id: f.id, requester: f.requester_id };
    });
    setFriendships(map);
  }, [user]);

  useEffect(() => { fetchProfiles(); }, [fetchProfiles]);
  useEffect(() => { fetchFriendships(); }, [fetchFriendships]);

  async function sendRequest(addresseeId: string) {
    if (!user) return;
    const { error } = await supabase.from('friendships').insert({ requester_id: user.id, addressee_id: addresseeId });
    if (error) { toast.error('Could not send request'); return; }
    toast.success('Partner request sent!');
    setFriendships(f => ({ ...f, [addresseeId]: { status: 'pending', id: '', requester: user.id } }));
  }

  async function acceptRequest(friendshipId: string, otherId: string) {
    await supabase.from('friendships').update({ status: 'accepted' }).eq('id', friendshipId);
    setFriendships(f => ({ ...f, [otherId]: { ...f[otherId], status: 'accepted' } }));
    toast.success('Partner accepted!');
  }

  function clearFilters() {
    setFilterArea(''); setFilterFrequency(''); setFilterLocation('');
  }

  const hasFilters = filterArea || filterFrequency || filterLocation;

  return (
    <Layout>
      <div className="max-w-7xl mx-auto px-4 py-8">
        {/* @section: search-header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-1">Find Partners</h1>
          <p className="text-muted-foreground">Discover people who share your goals and accountability style</p>
        </div>

        {/* Search + Filter Bar */}
        {/* @section: search-bar */}
        <div className="flex flex-col sm:flex-row gap-3 mb-4">
          <div className="relative flex-1">
            <SearchIcon className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name…"
              value={query}
              onChange={e => setQuery(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && fetchProfiles()}
              className="pl-10 h-11 rounded-xl"
            />
          </div>
          <Button variant="outline" onClick={() => setShowFilters(!showFilters)} className="h-11 rounded-xl gap-2">
            <SlidersHorizontal className="w-4 h-4" />
            Filters
            {hasFilters && <Badge className="ml-1 h-5 w-5 rounded-full p-0 flex items-center justify-center text-xs">!</Badge>}
          </Button>
          <Button onClick={fetchProfiles} className="h-11 rounded-xl px-6"
            style={{ boxShadow: '0 2px 8px color-mix(in srgb, var(--primary) 30%, transparent)' }}>
            Search
          </Button>
        </div>

        {/* Filters panel */}
        {showFilters && (
          <motion.div initial={{ opacity: 0, height: 0 }} animate={{ opacity: 1, height: 'auto' }}
            className="bg-muted/50 border border-border rounded-2xl p-4 mb-6 grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <MapPin className="w-3 h-3" /> Location
              </p>
              <Input placeholder="City or country…" value={filterLocation} onChange={e => setFilterLocation(e.target.value)} className="h-9 rounded-xl" />
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <Tag className="w-3 h-3" /> Area of Interest
              </p>
              <Select value={filterArea || '_all'} onValueChange={v => setFilterArea(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-9 rounded-xl"><SelectValue placeholder="Any area" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Any area</SelectItem>
                  {AREAS_OF_INTEREST.map(a => <SelectItem key={a} value={a}>{a}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider mb-2 flex items-center gap-1">
                <Clock className="w-3 h-3" /> Frequency
              </p>
              <Select value={filterFrequency || '_all'} onValueChange={v => setFilterFrequency(v === '_all' ? '' : v)}>
                <SelectTrigger className="h-9 rounded-xl"><SelectValue placeholder="Any frequency" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Any frequency</SelectItem>
                  {FREQUENCIES.map(f => <SelectItem key={f.value} value={f.value}>{f.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {hasFilters && (
              <div className="sm:col-span-3 flex justify-end">
                <Button variant="ghost" size="sm" onClick={clearFilters} className="text-muted-foreground text-xs">
                  <X className="w-3 h-3 mr-1" /> Clear filters
                </Button>
              </div>
            )}
          </motion.div>
        )}

        {/* Results */}
        {/* @section: search-results */}
        {loading ? (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {Array.from({ length: 8 }).map((_, i) => (
              <div key={i} className="bg-card border border-border rounded-2xl p-5 space-y-3">
                <div className="flex items-center gap-3">
                  <Skeleton className="w-12 h-12 rounded-full" />
                  <div className="space-y-2 flex-1">
                    <Skeleton className="h-4 w-24" />
                    <Skeleton className="h-3 w-16" />
                  </div>
                </div>
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-3/4" />
              </div>
            ))}
          </div>
        ) : profiles.length === 0 ? (
          <div className="text-center py-24">
            <SearchIcon className="w-12 h-12 text-muted-foreground/30 mx-auto mb-4" />
            <h3 className="font-semibold text-foreground mb-2">No members found</h3>
            <p className="text-muted-foreground text-sm">Try adjusting your search or clearing filters</p>
          </div>
        ) : (
          <motion.div variants={staggerContainer} initial="hidden" animate="visible"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
            {profiles.map(p => {
              const fs = friendships[p.id];
              const isFriend = fs?.status === 'accepted';
              const isPending = fs?.status === 'pending';
              const isIncoming = isPending && fs?.requester !== user?.id;

              return (
                <motion.div key={p.id} variants={staggerItem}
                  className="bg-card border border-border rounded-2xl p-5 flex flex-col gap-4 hover:border-primary/30 transition-all duration-200"
                  style={{ boxShadow: '0 2px 12px color-mix(in srgb, var(--primary) 5%, transparent)' }}>
                  {/* Header */}
                  <div className="flex items-start gap-3">
                    <button onClick={() => navigate(`/profile/${p.id}`)}>
                      <Avatar className="w-12 h-12">
                        <AvatarImage src={p.avatar_url ?? undefined} />
                        <AvatarFallback className="bg-primary/15 text-primary font-bold">
                          {(p.full_name || p.username)?.[0]?.toUpperCase()}
                        </AvatarFallback>
                      </Avatar>
                    </button>
                    <div className="flex-1 min-w-0">
                      <button onClick={() => navigate(`/profile/${p.id}`)} className="text-left">
                        <p className="font-semibold text-foreground text-sm truncate hover:text-primary transition-colors">
                          {p.full_name || p.username}
                        </p>
                      </button>
                      <p className="text-muted-foreground text-xs">@{p.username}</p>
                      {p.location && (
                        <p className="text-muted-foreground text-xs flex items-center gap-1 mt-0.5">
                          <MapPin className="w-2.5 h-2.5" /> {p.location}
                        </p>
                      )}
                    </div>
                  </div>

                  {/* Goal */}
                  {p.accountability_goal && (
                    <p className="text-foreground text-xs leading-relaxed line-clamp-2 bg-muted/50 rounded-lg px-3 py-2">
                      🎯 {p.accountability_goal}
                    </p>
                  )}

                  {/* Frequency */}
                  {p.preferred_frequency && (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Clock className="w-3 h-3" />
                      <span className="capitalize">{p.preferred_frequency} check-ins</span>
                    </div>
                  )}

                  {/* Areas */}
                  {p.areas_of_interest?.length > 0 && (
                    <div className="flex flex-wrap gap-1.5">
                      {p.areas_of_interest.slice(0, 3).map(a => (
                        <Badge key={a} variant="secondary" className="text-xs rounded-md px-2 py-0.5">{a}</Badge>
                      ))}
                      {p.areas_of_interest.length > 3 && (
                        <Badge variant="secondary" className="text-xs rounded-md px-2 py-0.5">+{p.areas_of_interest.length - 3}</Badge>
                      )}
                    </div>
                  )}

                  {/* Action buttons */}
                  <div className="mt-auto pt-2 flex gap-2">
                    {isFriend ? (
                      <Button size="sm" variant="outline" className="flex-1 rounded-xl text-xs h-8"
                        onClick={() => navigate(`/chat/${p.id}`)}>
                        <MessageSquare className="w-3.5 h-3.5 mr-1" /> Message
                      </Button>
                    ) : isIncoming ? (
                      <Button size="sm"
                        className="flex-1 rounded-xl text-xs h-8 bg-accent hover:bg-accent/90 text-accent-foreground border-0"
                        onClick={() => acceptRequest(fs.id, p.id)}
                        style={{ boxShadow: '0 2px 6px color-mix(in srgb, var(--accent) 30%, transparent)' }}>
                        <UserCheck className="w-3.5 h-3.5 mr-1" /> Accept
                      </Button>
                    ) : isPending ? (
                      <Button size="sm" variant="secondary" className="flex-1 rounded-xl text-xs h-8" disabled>
                        <Clock className="w-3.5 h-3.5 mr-1" /> Pending
                      </Button>
                    ) : (
                      <Button size="sm" className="flex-1 rounded-xl text-xs h-8"
                        onClick={() => sendRequest(p.id)}
                        style={{ boxShadow: '0 2px 6px color-mix(in srgb, var(--primary) 25%, transparent)' }}>
                        <UserPlus className="w-3.5 h-3.5 mr-1" /> Add Partner
                      </Button>
                    )}
                    <Button size="sm" variant="ghost" className="rounded-xl text-xs h-8 px-2"
                      onClick={() => navigate(`/profile/${p.id}`)}>
                      View
                    </Button>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        )}
      </div>
    </Layout>
  );
}
